import React from 'react';
import {
  AreaConfigs,
  EducationalInfo,
  InputQuestion,
  TemperatureSlider,
  TemperatureUnits,
  ThreeAreasPercentageSlider,
  useComponent,
  YesNoToggle,
  useFeatureAppId,
  generateTestId,
  TestIdElementType,
  TireSelection,
} from '@volkswagen-onehub/whitelabel-e-mobility-commons';
import {
  EducationalInfoData,
  PayloadUi,
  RangeTrimModel,
  TireSelectionOptions as TireSelectionTranslations,
  WEIGHT_PAYLOAD_STEP,
} from '@volkswagen-onehub/whitelabel-e-mobility-data-types';
import {RangeCalculationFormFields} from '../use-range-calculation-form';
import {useTenantConfiguration} from '../use-tenant-configuration';
import {useTranslation} from '../use-translation';
import {PayloadPeopleToggle} from './payload-people-toggle';

interface InputSectionProps {
  drivingProfileField: RangeCalculationFormFields['drivingProfile'];
  outsideTemperatureField: RangeCalculationFormFields['outsideTemperature'];
  payloadWeightField: RangeCalculationFormFields['payloadWeight'];
  payloadPeopleField: RangeCalculationFormFields['payloadPeople'];
  fasterHighwayField: RangeCalculationFormFields['fasterHighway'];
  airConditioningField: RangeCalculationFormFields['airConditioning'];
  isCommercial: boolean;
  payloadUI?: PayloadUi;
  selectedModel: RangeTrimModel;
  tireTypeField: RangeCalculationFormFields['tireType'];
  rimField: RangeCalculationFormFields['rim'];
}

export const InputSection: React.FC<InputSectionProps> = React.memo(
  ({
    drivingProfileField,
    outsideTemperatureField,
    payloadWeightField,
    payloadPeopleField,
    fasterHighwayField,
    airConditioningField,
    isCommercial,
    payloadUI,
    selectedModel,
    tireTypeField,
    rimField,
  }: InputSectionProps) => {
    const translations = useTranslation();
    const temperatureRangeDefaults = {min: -20, max: 30};
    const Icon = useComponent('Icon');
    const featureAppId = useFeatureAppId();
    const rangeTenantConfig = useTenantConfiguration();

    const sliderAreasDummyConfig = React.useMemo(
      (): AreaConfigs => [
        {
          name: translations.inputSection.drivingLocationsOptions.nameCity,
          icon: <Icon name="LocationPublic" size="medium" ariaHidden />,
          initialValue: drivingProfileField.value[0],
        },
        {
          name: translations.inputSection.drivingLocationsOptions.nameRoad,
          icon: <Icon name="LocationCountryRoad" size="medium" ariaHidden />,
          initialValue: drivingProfileField.value[1],
        },
        {
          name: translations.inputSection.drivingLocationsOptions.nameMotorway,
          icon: <Icon name="LocationHighway" size="medium" ariaHidden />,
          initialValue: drivingProfileField.value[2],
        },
      ],
      // eslint-disable-next-line react-hooks/exhaustive-deps -- all the other potential dependencies of this hook are default values and will not change.
      [translations]
    );

    const outsideTemperatureDefaultValue = React.useMemo(
      () => outsideTemperatureField.value,
      // eslint-disable-next-line react-hooks/exhaustive-deps -- We don't want to update the defaultValues on each change, as it resets the slider states
      []
    );

    const payloadWeightDefaultValue = React.useMemo(
      () => payloadWeightField.value,
      // eslint-disable-next-line react-hooks/exhaustive-deps -- We don't want to update the defaultValues on each change, as it resets the slider states
      []
    );

    const rangeTenantConfiguration = useTenantConfiguration();

    const {
      drivingLocationsQuestion,
      highwaySpeedQuestion,
      temperatureQuestion,
      airConditionQuestion,
      payloadQuestion,
      passengersQuestion,
    } = rangeTenantConfiguration.inputSection;

    const highwaySwitchEnabled = React.useMemo(() => {
      const enabledForPrivate =
        rangeTenantConfiguration.inputSection?.highwaySpeedQuestion
          ?.enabledForPrivate;

      if (isCommercial) {
        return false;
      }

      return Boolean(enabledForPrivate);
    }, [rangeTenantConfiguration, isCommercial]);

    const Slider = useComponent('Slider');

    function getTemperatureUnit(): TemperatureUnits {
      const unit = rangeTenantConfiguration.global.temperatureUnit;
      switch (unit) {
        case 'Celsius':
          return TemperatureUnits.celsius;
        case 'Fahrenheit':
          return TemperatureUnits.fahrenheit;
      }
    }

    const tempUnit = getTemperatureUnit();
    const getTemperatureValueText = React.useCallback(
      (value: number) =>
        translations.inputSection.temperatureOptions[tempUnit].replace(
          '%%temperature%%',
          `${value >= 0 ? '+' : '-'}${Math.abs(value)}`
        ),
      [translations, tempUnit]
    );

    const getPayloadValueText = React.useCallback(
      (value: number) =>
        translations.inputSection.payloadOptions.weightKG.replace(
          '%%weight%%',
          value.toString()
        ),
      [translations]
    );

    return (
      <section aria-label={translations.inputSection.ariaLabel}>
        <InputQuestion
          text={drivingProfileField.label}
          questionTextId={drivingProfileField.labelId}
          infoButton={
            <EducationalInfo
              translations={translations.inputSection.drivingLocationsQuestion}
              globalTranslations={translations.global}
              faqSectionTranslations={translations.faqSection}
              faqOrLink={drivingLocationsQuestion}
            />
          }
        >
          <ThreeAreasPercentageSlider
            id={drivingProfileField.id}
            labelId={drivingProfileField.labelId}
            areas={sliderAreasDummyConfig}
            onChangeImmediate={drivingProfileField.onChangeWithoutSideEffect}
            onChange={drivingProfileField.onChange}
          />
        </InputQuestion>
        {highwaySwitchEnabled && (
          <InputQuestion
            text={fasterHighwayField.label}
            questionTextId={fasterHighwayField.labelId}
            infoButton={
              <EducationalInfo
                translations={translations.inputSection.highwaySpeedQuestion}
                globalTranslations={translations.global}
                faqSectionTranslations={translations.faqSection}
                faqOrLink={highwaySpeedQuestion}
              />
            }
          >
            <YesNoToggle
              id={fasterHighwayField.id}
              value={fasterHighwayField.value}
              onChange={fasterHighwayField.onChange}
              questionTextId={fasterHighwayField.labelId}
              responseLabels={translations.global.responseLabels}
              testId={generateTestId({
                featureAppId,
                elementName: 'highway-plus',
                elementType: TestIdElementType.toggle,
              })}
            />
          </InputQuestion>
        )}

        {((translations.inputSection.tireSelectionQuestion &&
          translations.inputSection.tireSelectionOptions) ||
          translations.carSelectionSection.tireSelection) && (
          <InputQuestion
            text={
              translations.inputSection.tireSelectionQuestion?.heading ||
              translations.carSelectionSection.tireSelection?.heading ||
              ''
            }
            questionTextId={`${featureAppId}_tire-selection-question`}
            infoButton={
              <EducationalInfo
                translations={
                  (translations.inputSection
                    .tireSelectionQuestion as EducationalInfoData) ||
                  ({
                    heading:
                      translations.carSelectionSection.tireSelection?.heading,
                    educationalInfoContent:
                      translations.carSelectionSection.tireSelection
                        ?.educationalInfoContent,
                    educationalInfoLinkText:
                      translations.carSelectionSection.tireSelection
                        ?.educationalInfoLinkText,
                    educationalInfoInfoButtonLabel:
                      translations.carSelectionSection.tireSelection
                        ?.educationalInfoInfoButtonLabel,
                  } as EducationalInfoData)
                }
                globalTranslations={translations.global}
                faqSectionTranslations={translations.faqSection}
                faqOrLink={
                  rangeTenantConfig.carSelectionSection.tireSelection ||
                  rangeTenantConfig.inputSection.tireSelection
                }
              />
            }
          >
            <TireSelection
              selectedModel={selectedModel}
              tireTypeField={tireTypeField}
              rimField={rimField}
              tireSelectionTranslations={
                (translations.inputSection
                  .tireSelectionOptions as TireSelectionTranslations) ||
                ({
                  disclaimerFootnoteShort:
                    translations.carSelectionSection.tireSelection
                      ?.disclaimerFootnoteShort,
                  disclaimerFootnoteLink:
                    translations.carSelectionSection.tireSelection
                      ?.disclaimerFootnoteLink,
                  summerTireLabel:
                    translations.carSelectionSection.tireSelection
                      ?.summerTireLabel,
                  winterTireLabel:
                    translations.carSelectionSection.tireSelection
                      ?.winterTireLabel,
                  allSeasonLabel:
                    translations.carSelectionSection.tireSelection
                      ?.allSeasonLabel,
                  tireLabel:
                    translations.carSelectionSection.tireSelection?.tireLabel,
                  rimLabel:
                    translations.carSelectionSection.tireSelection?.rimLabel,
                } as TireSelectionTranslations)
              }
              blacklistRims={selectedModel.blacklistRims}
            />
          </InputQuestion>
        )}

        <InputQuestion
          text={outsideTemperatureField.label}
          questionTextId={outsideTemperatureField.labelId}
          infoButton={
            <EducationalInfo
              translations={translations.inputSection.temperatureQuestion}
              globalTranslations={translations.global}
              faqSectionTranslations={translations.faqSection}
              faqOrLink={temperatureQuestion}
            />
          }
        >
          <TemperatureSlider
            ariaLabelledby={outsideTemperatureField.labelId}
            defaultValue={outsideTemperatureDefaultValue}
            onChangeImmediate={
              outsideTemperatureField.onChangeWithoutSideEffect
            }
            onChange={outsideTemperatureField.onChange}
            tempUnit={tempUnit}
            getValueText={getTemperatureValueText}
            range={temperatureQuestion.range ?? temperatureRangeDefaults}
          />
        </InputQuestion>
        <InputQuestion
          text={airConditioningField.label}
          questionTextId={airConditioningField.labelId}
          infoButton={
            <EducationalInfo
              translations={translations.inputSection.airConditionQuestion}
              globalTranslations={translations.global}
              faqSectionTranslations={translations.faqSection}
              faqOrLink={airConditionQuestion}
            />
          }
        >
          <YesNoToggle
            id="air-con-toggle"
            questionTextId={airConditioningField.labelId}
            value={airConditioningField.value}
            onChange={airConditioningField.onChange}
            responseLabels={translations.global.responseLabels}
            testId={generateTestId({
              featureAppId,
              elementName: 'air-condition',
              elementType: TestIdElementType.toggle,
            })}
          />
        </InputQuestion>

        {payloadUI?.kind === 'slider' ? (
          <InputQuestion
            questionTextId={payloadWeightField.labelId}
            text={payloadWeightField.label}
            containerSpacingBottom={false}
            infoButton={
              <EducationalInfo
                translations={translations.inputSection.payloadQuestion}
                globalTranslations={translations.global}
                faqSectionTranslations={translations.faqSection}
                faqOrLink={payloadQuestion}
              />
            }
          >
            <Slider
              min={0}
              max={payloadUI.maxPayload}
              step={WEIGHT_PAYLOAD_STEP}
              getValueText={getPayloadValueText}
              defaultValue={payloadWeightDefaultValue}
              onChangeImmediate={payloadWeightField.onChangeWithoutSideEffect}
              onChange={payloadWeightField.onChange}
              labelId={payloadWeightField.labelId}
            />
          </InputQuestion>
        ) : (
          <InputQuestion
            questionTextId={payloadPeopleField.labelId}
            text={payloadPeopleField.label}
            containerSpacingBottom={false}
            infoButton={
              <EducationalInfo
                translations={translations.inputSection.passengersQuestion}
                globalTranslations={translations.global}
                faqSectionTranslations={translations.faqSection}
                faqOrLink={passengersQuestion}
              />
            }
          >
            <PayloadPeopleToggle
              value={payloadPeopleField.value}
              onChange={payloadPeopleField.onChange}
              id={payloadPeopleField.id}
              questionTextId={payloadPeopleField.labelId}
              testId={generateTestId({
                featureAppId,
                elementName: 'payload-people',
                elementType: TestIdElementType.toggle,
              })}
            />
          </InputQuestion>
        )}
      </section>
    );
  }
);

InputSection.displayName = 'InputSection';
